// Break detection + summary aggregations for the Fixed Income recon grid.
//
// This desk reconciles on UNITS (par/quantity), not on FV/BV value. A row is a
// break when its ORIGINAL or CURRENT units alert is set, or the position is
// one-sided (NOT MATCHING). The unit alerts are the same values that drive the
// grid's orange/red cell coloring (computed server-side by compute_unit_alerts):
//   ORIG_UNITS_ALERT / CURR_UNITS_ALERT = number of consecutive month-ends the
//   diff has persisted (0 = none, 1-2 = recent, 3+ = persistent), carried on the
//   current-month row of each position. FV/BV value breaks are intentionally
//   NOT treated as breaks here.

export const NOT_MATCHING = 'NOT MATCHING';

/** Generic counter: number of rows matching a predicate (safe on non-arrays). */
export const countWhere = (rows, predicate) =>
  Array.isArray(rows) ? rows.reduce((n, r) => n + (predicate(r) ? 1 : 0), 0) : 0;

/** One-sided position: present on only the custody or the Clearwater side. */
export const isNotMatching = (row) =>
  !!row && (row.REC_FV_ALERT === NOT_MATCHING || row.REC_BV_ALERT === NOT_MATCHING);

/** Current-month break in ORIGINAL units (the orig-units diff is non-zero). */
export const isOrigUnitBreak = (row) => !!row && Number(row.ORIG_UNITS_ALERT) >= 1;

/** Current-month break in CURRENT units (the curr-units diff is non-zero). */
export const isCurrUnitBreak = (row) => !!row && Number(row.CURR_UNITS_ALERT) >= 1;

/** Persistent unit break: orig or curr units diff has held for 3+ month-ends. */
export const isPersistentBreak = (row) =>
  !!row && (Number(row.ORIG_UNITS_ALERT) >= 3 || Number(row.CURR_UNITS_ALERT) >= 3);

/** A break for this desk: an orig or curr units break, or a one-sided position. */
export const isBreak = (row) => isOrigUnitBreak(row) || isCurrUnitBreak(row) || isNotMatching(row);

/** Subset of rows that are breaks (safe on non-arrays). */
export const filterBreaks = (rows) => (Array.isArray(rows) ? rows.filter(isBreak) : []);

/** Count of breaking rows. */
export const countBreaks = (rows) => countWhere(rows, isBreak);
